import { authenticatedApi } from "@/api";
import { AppModal, RewardContent } from "@/components/AppModal";
import { MultiplayerIcon, SettingsIcon, SinglePlayerIcon, StoreIcon } from "@/components/icons";
import { LandscapeLayout } from "@/components/layouts";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export function HomeScreen() {
  const { username, token } = useAuth();
  const navigate = useNavigate();
  const [showDailyReward, setShowDailyReward] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [selectedOpponentType, setSelectedOpponentType] = useState<"human" | "cpu" | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const handleOpponentSelect = (opponentType: "human" | "cpu") => {
    setSelectedOpponentType(opponentType);
    setShowModeModal(true);
  };

  const handleModeSelect = (gameMode: string) => {
    setShowModeModal(false);
    if (selectedOpponentType) {
      navigate("/matchmaking", {
        state: {
          opponentType: selectedOpponentType,
          gameMode
        }
      });
    }
  };

  // Temporary BundleX purchase handler
  const handleBuyBundleX = async () => {
    if (!token) {
      alert("Please log in to make purchases");
      return;
    }

    setPurchaseLoading(true);
    try {
      // Generate unique idempotency key for this purchase attempt
      const idempotencyKey = crypto.randomUUID();

      const client = authenticatedApi(token);
      const response = await client.v1.purchase.initiate.$post({
        json: {
          bundleId: "bundle-x-001",
          idempotencyKey: idempotencyKey
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.invoiceLink) {
          // Use Telegram WebApp API to show payment overlay
          if (window.Telegram?.WebApp?.openInvoice) {
            window.Telegram.WebApp.openInvoice(
              data.invoiceLink,
              (status: "paid" | "cancelled" | "failed" | "pending") => {
                setPurchaseLoading(false); // Reset loading state after payment interaction

                if (status === "paid") {
                  alert("🎉 Payment successful! Your BundleX has been activated!");
                } else if (status === "cancelled") {
                  alert("Payment was cancelled.");
                } else if (status === "failed") {
                  alert("Payment failed. Please try again.");
                }
              }
            );
          } else {
            // Fallback for testing outside Telegram
            window.open(data.invoiceLink, "_blank");
          }
        } else {
          alert(`Success! ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Purchase failed: ${errorData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Purchase error:", error);
      alert("Purchase failed. Please try again.");
    } finally {
      // Only reset loading if not using Telegram WebApp (callback will handle it)
      if (!window.Telegram?.WebApp?.openInvoice) {
        setPurchaseLoading(false);
      }
    }
  };

  return (
    <LandscapeLayout>
      {/* Main Content - Split Layout */}
      <div className="flex-1 flex items-center justify-between px-4 sm:px-8 lg:px-12 py-6 lg:py-8">
        {/* Left Side - Branding */}
        <div className="flex flex-col justify-center flex-shrink-0">
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-2 lg:mb-4 text-amber-400 tracking-wider">
            MoonGammon
          </h1>
          <p className="text-lg sm:text-xl lg:text-3xl text-amber-200">
            Welcome, {username || "Player"}!
          </p>
        </div>

        {/* Right Side - Vertical Button List */}
        <div className="flex flex-col gap-2 sm:gap-3 lg:gap-4">
          <button
            onClick={() => handleOpponentSelect("human")}
            className="relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 active:shadow-md h-12 sm:h-14 lg:h-16 flex items-center justify-start px-3 sm:px-4 w-48 sm:w-56 lg:w-64"
          >
            <div className="relative z-10 flex items-center">
              <MultiplayerIcon size={18} className="mr-2 sm:mr-3" />
              <span className="text-xs sm:text-sm">Online Opponent</span>
            </div>
          </button>

          <button
            onClick={() => handleOpponentSelect("cpu")}
            className="relative overflow-hidden bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 active:shadow-md h-12 sm:h-14 lg:h-16 flex items-center justify-start px-3 sm:px-4 w-48 sm:w-56 lg:w-64"
          >
            <div className="relative z-10 flex items-center">
              <SinglePlayerIcon size={18} className="mr-2 sm:mr-3" />
              <span className="text-xs sm:text-sm">Single Player</span>
            </div>
          </button>

          <Link
            to="/store"
            className="relative overflow-hidden bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 active:shadow-md h-12 sm:h-14 lg:h-16 flex items-center justify-start px-3 sm:px-4 w-48 sm:w-56 lg:w-64"
          >
            <div className="relative z-10 flex items-center">
              <StoreIcon size={18} className="mr-2 sm:mr-3" />
              <span className="text-xs sm:text-sm">Store</span>
            </div>
          </Link>

          <Link
            to="/profile"
            className="relative overflow-hidden bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 active:shadow-md h-12 sm:h-14 lg:h-16 flex items-center justify-start px-3 sm:px-4 w-48 sm:w-56 lg:w-64"
          >
            <div className="relative z-10 flex items-center">
              <SettingsIcon size={18} className="mr-2 sm:mr-3" />
              <span className="text-xs sm:text-sm">Settings</span>
            </div>
          </Link>

          {/* Demo Daily Reward Button */}
          <button
            onClick={() => setShowDailyReward(true)}
            className="relative overflow-hidden bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 active:shadow-md h-12 sm:h-14 lg:h-16 flex items-center justify-start px-3 sm:px-4 w-48 sm:w-56 lg:w-64"
          >
            <div className="relative z-10 flex items-center">
              <span className="text-xl mr-2 sm:mr-3">🎁</span>
              <span className="text-xs sm:text-sm">Daily Reward (Demo)</span>
            </div>
          </button>

          {/* Temporary BundleX Purchase Button */}
          <button
            onClick={handleBuyBundleX}
            disabled={purchaseLoading}
            className="relative overflow-hidden bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 active:shadow-md h-12 sm:h-14 lg:h-16 flex items-center justify-start px-3 sm:px-4 w-48 sm:w-56 lg:w-64 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            <div className="relative z-10 flex items-center">
              <span className="text-xl mr-2 sm:mr-3">⭐</span>
              <span className="text-xs sm:text-sm">
                {purchaseLoading ? "Purchasing..." : "Buy BundleX - 100⭐"}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full text-center py-2 text-xs text-indigo-300">
        <p>© {new Date().getFullYear()} NextGammon • v2.0.0</p>
      </div>

      {/* Daily Reward Demo Modal */}
      <AppModal
        show={showDailyReward}
        variant="reward"
        rarity="legendary"
        title="Daily Login Bonus!"
        actions={[
          {
            label: "Claim",
            onClick: () => setShowDailyReward(false),
            variant: "primary"
          }
        ]}
      >
        <RewardContent
          type="Gold Coins"
          amount={500}
          rarity="legendary"
          icon="💰"
          description="Thanks for playing every day!"
        />
      </AppModal>

      {/* Game Mode Selection Modal */}
      <AppModal
        show={showModeModal}
        variant="info"
        title="Select Game Mode"
        actions={[
          {
            label: "Cancel",
            onClick: () => setShowModeModal(false),
            variant: "secondary"
          }
        ]}
      >
        <div className="grid grid-cols-2 gap-4 p-4">
          <button
            onClick={() => handleModeSelect("standard")}
            className="p-4 bg-gray-700 hover:bg-amber-600 rounded-lg transition-colors duration-200 text-center"
          >
            <div className="text-2xl mb-2">⏱️</div>
            <h3 className="font-bold text-white mb-1">Standard</h3>
            <p className="text-xs text-gray-300">60 seconds per move</p>
            <p className="text-xs text-gray-400 mt-1">Classic timing</p>
          </button>

          <button
            onClick={() => handleModeSelect("speedgammon")}
            className="p-4 bg-gray-700 hover:bg-amber-600 rounded-lg transition-colors duration-200 text-center"
          >
            <div className="text-2xl mb-2">⚡</div>
            <h3 className="font-bold text-white mb-1">Speedgammon</h3>
            <p className="text-xs text-gray-300">2 minute time bank</p>
            <p className="text-xs text-gray-400 mt-1">10s free per move</p>
          </button>
        </div>
      </AppModal>
    </LandscapeLayout>
  );
}

export default HomeScreen;
